import apiClient from "@/lib/apiClient";
import type {
  NotificationItem,
  NotificationListParams,
  NotificationListResult,
  NotificationPreference,
} from "../types/notifications.types";

const BASE = "/api/users/me";

/**
 * Nest ApiGetAll interceptors wrap controller returns as:
 *   { status, data: { success, status, data: <controllerPayload>, message } }
 * ApiGetOne / ApiUpdate produce the same shape. `unwrap` walks both single- and
 * double-wrapped responses and always returns the controller payload.
 */
function unwrap<T>(raw: unknown): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = raw as any;
  if (r && typeof r === "object") {
    if ("data" in r) {
      const inner = r.data;
      if (inner && typeof inner === "object" && "data" in inner) {
        return inner.data as T;
      }
      return inner as T;
    }
  }
  return raw as T;
}

export async function listNotifications(
  params?: NotificationListParams,
): Promise<NotificationListResult> {
  const { data } = await apiClient.get(`${BASE}/notifications`, {
    params: {
      page: params?.page ?? 1,
      limit: params?.limit ?? 10,
      unreadOnly: params?.unreadOnly ? "true" : undefined,
    },
  });
  return unwrap<NotificationListResult>(data);
}

export async function getUnreadCount(): Promise<number> {
  const { data } = await apiClient.get(`${BASE}/notifications/unread-count`);
  const payload = unwrap<{ count: number }>(data);
  return payload?.count ?? 0;
}

export async function markNotificationAsRead(
  id: string,
): Promise<NotificationItem> {
  const { data } = await apiClient.patch(`${BASE}/notifications/${id}/read`);
  return unwrap<NotificationItem>(data);
}

export async function markAllNotificationsAsRead(): Promise<number> {
  const { data } = await apiClient.patch(`${BASE}/notifications/read-all`);
  const payload = unwrap<{ updated: number }>(data);
  return payload?.updated ?? 0;
}

export async function getNotificationPreferences(): Promise<
  NotificationPreference[]
> {
  const { data } = await apiClient.get(`${BASE}/notification-preferences`);
  const payload = unwrap<NotificationPreference[]>(data);
  return Array.isArray(payload) ? payload : [];
}

export async function updateNotificationPreferences(
  prefs: NotificationPreference[],
): Promise<NotificationPreference[]> {
  const { data } = await apiClient.patch(
    `${BASE}/notification-preferences`,
    prefs,
  );
  const payload = unwrap<NotificationPreference[]>(data);
  return Array.isArray(payload) ? payload : [];
}

export const notificationsApi = {
  listNotifications,
  getUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  getNotificationPreferences,
  updateNotificationPreferences,
};
